import { useEffect, useRef, useState } from "react";
import { DataGrid } from "@mui/x-data-grid";
import jwtInterceoptor from "./shared/jwtInterceptor";
import { baseURL } from "../services/api";
import moment from "moment";
import UploadDocument from "./UploadDocument";
import { toast } from "react-toastify"; // Import toast
import "react-toastify/dist/ReactToastify.css"; // Import toast styles

export default function Documents({ open, handleClose, organisationId }) {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteLoading, setDeleteLoading] = useState(false); // New state for delete loading
  const [error, setError] = useState(null);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const offcanvasRef = useRef(null);
  const [showUpload, setShowUpload] = useState(false);

  useEffect(() => {
    const fetchDocuments = async () => {
      try {
        setLoading(true);
        setError(null);
        setDocuments([]);
        setSelectedDocument(null);

        if (!organisationId) {
          setLoading(false);
          return;
        }

        const response = await jwtInterceoptor.get(
          `${baseURL}/data/documents/${organisationId}`,
        );
        setDocuments(response.data);
      } catch (err) {
        setError("Erreur lors du chargement des documents.");
      } finally {
        setLoading(false);
        console.log("Documents loaded:", documents);
      }
    };

    fetchDocuments();
  }, []);

  // Ouvrir / fermer l'offcanvas selon la prop `open`
  useEffect(() => {
    const el = offcanvasRef.current;
    if (!el || !window.bootstrap) return;
    const instance = window.bootstrap.Offcanvas.getOrCreateInstance(el);
    if (open) {
      instance.show();
    } else {
      instance.hide();
    }
  }, [open]);

  // Prévenir le parent quand Bootstrap ferme l'offcanvas (X, backdrop, Echap)
  useEffect(() => {
    const el = offcanvasRef.current;
    if (!el) return;
    const onHidden = () => {
      setShowUpload(false); // revenir à la liste pour la prochaine ouverture
      handleClose();
    };
    el.addEventListener("hidden.bs.offcanvas", onHidden);
    return () => el.removeEventListener("hidden.bs.offcanvas", onHidden);
  }, [handleClose]);

  const handleDownload = async (doc) => {
    try {
      const response = await jwtInterceoptor.get(
        `${baseURL}/data/documents/${doc.id}/download`,
        { responseType: "blob" },
      );

      console.log("Download response:", response.data);
      console.log("Document ID:", doc.id);
      console.log("Document Name:", doc.nom_fichier);

      // Extract the file extension
      const extension = doc.nom_fichier.split(".").pop();
      const fileNameWithExtension = `${doc.nom_fichier}.${extension}`;

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", fileNameWithExtension); // Use the file name with extension
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("Document a été téléchargé avec succès!");
    } catch (err) {
      console.error("Erreur lors du téléchargement:", err);
      toast.error("Erreur lors du téléchargement du document.");
    }
  };

  const handleDelete = async (doc) => {
    try {
      setDeleteLoading(true); // Set delete loading to true
      console.log("Deleting:", doc);
      await jwtInterceoptor.delete(`${baseURL}/data/documents/${doc.id}`);
      setDocuments((prev) => prev.filter((d) => d.id !== doc.id));
      toast.success("Document a été supprimé avec succès!"); // Show success toast
    } catch (err) {
      console.error("Erreur lors de la suppression:", err);
      toast.error("Erreur lors de la suppression du document."); // Show error toast
    } finally {
      setDeleteLoading(false); // Reset delete loading
    }
  };

  return (
    <div
      ref={offcanvasRef}
      className="offcanvas offcanvas-end documents-offcanvas"
      tabIndex="-1"
      id="documentsOffcanvas"
      aria-labelledby="documentsOffcanvasLabel"
    >
      {/* Header */}
      <div className="offcanvas-header border-bottom">
        <div>
          <h5 className="offcanvas-title mb-1" id="documentsOffcanvasLabel">
            Documents Juridiques
          </h5>

          <p className="text-muted mb-0">
            Vous pouvez consulter vos documents juridiques ici.
          </p>
        </div>
        <button
          type="button"
          className="btn-close text-reset"
          data-bs-dismiss="offcanvas"
          aria-label="Close"
        >
          <i className="fe fe-x fs-18"></i>
        </button>
      </div>

      {/* Body */}
      <div className="offcanvas-body d-flex flex-column">
        {/* Add button */}
        <div className="d-flex justify-content-end mb-3">
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => setUploadModalOpen(true)}
          >
            <i className="fe fe-plus me-1"></i>
            Ajouter un document
          </button>
        </div>

        {/* Documents table */}
        <div
          style={{
            height: "400px",
            width: "100%",
          }}
        >
          <DataGrid
            rows={documents.map((doc, index) => ({
              id: index,
              nom_fichier: doc.nom_fichier,
              date_importation: moment(doc.created_at).format("DD/MM/YYYY"),
              actions: doc,
            }))}
            columns={[
              {
                field: "nom_fichier",
                headerName: "Nom du Fichier",
                flex: 1,
              },
              {
                field: "date_importation",
                headerName: "Date d'Importation",
                flex: 1,
              },
              {
                field: "actions",
                headerName: "Actions",
                flex: 1,
                renderCell: (params) => (
                  <div
                    style={{
                      display: "flex",
                      gap: "10px",
                    }}
                  >
                    <button
                      type="button"
                      className="btn btn-sm btn-warning"
                      title="Télécharger"
                      onClick={() => handleDownload(params.value)}
                    >
                      <span className="fe fe-download"></span>
                    </button>

                    <button
                      type="button"
                      className="btn btn-sm btn-danger"
                      title="Supprimer"
                      onClick={() => handleDelete(params.value)}
                      disabled={deleteLoading}
                    >
                      <span className="fe fe-trash-2"></span>
                    </button>
                  </div>
                ),
              },
            ]}
            disableRowSelectionOnClick
            loading={loading}
          />
        </div>

        {/* Footer */}
        <div className="d-flex justify-content-end mt-3 pt-3 border-top">
          <button
            onClick={handleClose}
            type="button"
            className="btn btn-danger"
          >
            Fermer
          </button>
        </div>
      </div>

      {/* Upload Document */}
      <UploadDocument
        open={uploadModalOpen}
        onClose={() => setUploadModalOpen(false)}
        organisationId={organisationId}
        onUploadSuccess={() => {
          setUploadModalOpen(false);
          setLoading(true);

          jwtInterceoptor
            .get(`${baseURL}/data/documents/${organisationId}`)
            .then((res) => setDocuments(res.data))
            .catch((error) => setError(error))
            .finally(() => setLoading(false));
        }}
      />
    </div>
  );
}
